class BYOBDrawer {
  constructor() {
    this.overlay = document.getElementById('byob-overlay');
    this.drawer = document.getElementById('byob-drawer');
    this.closeBtn = document.getElementById('byob-close');
    this.checkoutBtn = document.getElementById('byob-checkout-btn');
    
    this.inBoxList = document.getElementById('byob-in-box-list');
    this.addMoreList = document.getElementById('byob-add-more-list');
    this.inBoxTitle = document.getElementById('byob-in-box-title');
    
    this.progressText = document.getElementById('byob-progress-text');
    this.progressStatus = document.getElementById('byob-progress-status');
    this.progressBar = document.querySelector('.byob-drawer__progress-fill');
    this.subtitle = document.getElementById('byob-subtitle');
    
    this.runningTotal = document.getElementById('byob-running-total');
    this.servingText = document.getElementById('byob-serving-text');
    
    this.freqSection = document.getElementById('byob-freq-section');
    this.freqSelect = document.getElementById('byob-frequency-select');
    this.freeGiftBlock = document.querySelector('.byob-drawer__free-gift');
    
    this.productsData = JSON.parse(document.getElementById('byob-products-data').textContent);
    this.selections = {}; // format: { productId: quantity }
    this.minItems = 6;
    this.isReviewMode = false;
    
    this.init();
  }

  init() {
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.checkoutBtn.addEventListener('click', () => this.handleCheckout());
    this.freqSelect.addEventListener('change', () => this.updateUI());
    
    // Intercept clicks on any "Start my box" button on the page
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.obp-cta--main, .obp-mb__b, .block__redirect_to-bundle a, [data_main-product]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        
        let currentProductHandle = null;
        if (trigger.href && trigger.href.includes('add=')) {
          currentProductHandle = new URL(trigger.href, window.location.origin).searchParams.get('add');
        } else if (trigger.closest('.block__redirect_to-bundle')) {
          currentProductHandle = trigger.closest('.block__redirect_to-bundle').getAttribute('data_main-product');
        } else if (trigger.getAttribute('data_main-product')) {
          currentProductHandle = trigger.getAttribute('data_main-product');
        }
        
        this.open(currentProductHandle);
      }
    });

    this.restoreState();
    this.renderLists();
  }

  restoreState() {
    try {
      const stored = sessionStorage.getItem('ob_pdp_byob_state');
      if (stored) {
        this.selections = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to restore drawer state');
    }
  }

  open(productHandle) {
    this.isReviewMode = false;
    this.freqSection.classList.remove('active');
    
    if (productHandle) {
      // Find product id by handle
      const targetTitle = productHandle.toLowerCase().trim();
      let foundId = null;
      for (const [id, data] of Object.entries(this.productsData)) {
        if (data.title.toLowerCase().trim() === targetTitle || data.handle === targetTitle) {
          foundId = id; break;
        }
      }
      
      if (foundId && !this.selections[foundId]) {
        this.sourceProductId = foundId;
        this.selections[foundId] = 1;
        this.subtitle.textContent = `${this.productsData[foundId].title} added — pick the rest`;
      }
    }
    
    this.updateUI();
    this.overlay.classList.add('active');
    this.drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('byob-drawer-open');
  }

  close() {
    this.overlay.classList.remove('active');
    this.drawer.classList.remove('active');
    document.body.style.overflow = '';
    document.body.classList.remove('byob-drawer-open');
  }

  updateQuantity(productId, delta) {
    const current = this.selections[productId] || 0;
    const next = current + delta;
    if (next < 0) return;
    
    if (next === 0) {
      delete this.selections[productId];
    } else {
      this.selections[productId] = next;
    }
    this.updateUI();
  }

  updateUI() {
    this.renderLists();
    
    let totalQty = 0;
    let totalPrice = 0;
    let totalPortions = 0;
    
    for (const [id, qty] of Object.entries(this.selections)) {
      totalQty += qty;
      totalPrice += (this.productsData[id].price * qty);
      totalPortions += ((this.productsData[id].portions || 2) * qty);
    }
    
    // Update progress
    this.progressText.innerHTML = `<strong>${totalQty}</strong> / ${this.minItems} minimum`;
    const progressPct = Math.min(100, (totalQty / this.minItems) * 100).toFixed(1);
    
    // Update all progress fills on the page to guarantee the visible one is caught
    document.querySelectorAll('.byob-drawer__progress-fill').forEach(fillEl => {
      fillEl.style.width = `${progressPct}%`;
      fillEl.style.backgroundColor = '#E6740E';
    });
    
    if (totalQty >= this.minItems) {
      this.progressStatus.textContent = 'Minimum reached';
      this.progressStatus.style.color = 'var(--ob-success, #2d6a4f)';
      this.checkoutBtn.disabled = false;
      this.checkoutBtn.classList.add('ob-cta-pulse');
    } else {
      const remainder = this.minItems - totalQty;
      this.progressStatus.textContent = `${remainder} more to go`;
      this.progressStatus.style.color = '';
      this.checkoutBtn.disabled = true;
      this.checkoutBtn.classList.remove('ob-cta-pulse');
    }
    
    // Update totals
    const formattedPrice = (totalPrice / 100).toFixed(0);
    this.runningTotal.textContent = `$${formattedPrice}`;
    
    if (totalQty > 0) {
      const pricePerPortion = (totalPrice / 100 / totalPortions).toFixed(2);
      this.servingText.textContent = `$${pricePerPortion} per serving · ${totalPortions} portions`;
      this.checkoutBtn.textContent = this.isReviewMode ? `Checkout · $${formattedPrice}` : `Next step · $${formattedPrice}`;
    } else {
      this.servingText.textContent = `$0 per serving · 0 portions`;
      this.checkoutBtn.textContent = `Next step · $0`;
    }
    
    // Toggle free gift based on frequency
    if (this.isReviewMode) {
      if (this.freqSelect.value === 'onetime') {
        this.freeGiftBlock.style.display = 'none';
      } else {
        this.freeGiftBlock.style.display = 'flex';
      }
    } else {
      // While building the box, always show the free gift preview since default is subscription
      this.freeGiftBlock.style.display = 'flex';
    }
    
    this.saveState();
  }

  saveState() {
    try {
      if (Object.keys(this.selections).length > 0) {
        sessionStorage.setItem('ob_pdp_byob_state', JSON.stringify(this.selections));
      } else {
        sessionStorage.removeItem('ob_pdp_byob_state');
      }
    } catch (e) {}
  }

  createItemHTML(id, product, qty, isInBox) {
    const priceStr = `$${(product.price / 100).toFixed(0)}`;
    const portions = product.portions || 2;
    const pricePerPortion = `$${(product.price / 100 / portions).toFixed(2)}`;
    const borderClass = isInBox ? 'byob-drawer__item--selected' : '';
    const fromPageLabel = (id === this.sourceProductId && isInBox) 
      ? `<div style="color: #E6740E; font-size: 10px; font-weight: 700; text-transform: uppercase; margin-top: 4px; letter-spacing: 0.5px;">FROM THE PAGE YOU WERE ON</div>` 
      : '';
    
    return `
      <div class="byob-drawer__item ${borderClass}">
        <img src="${product.image}" class="byob-drawer__item-img" alt="${product.title}">
        <div class="byob-drawer__item-info">
          <h4 class="byob-drawer__item-title">${product.title}</h4>
          <p class="byob-drawer__item-meta">${priceStr} · ${portions} portions · ${pricePerPortion}/portion</p>
          ${fromPageLabel}
        </div>
        <div class="byob-drawer__qty">
          <button onclick="window.byobDrawer.updateQuantity('${id}', -1)">−</button>
          <span>${qty}</span>
          <button onclick="window.byobDrawer.updateQuantity('${id}', 1)">+</button>
        </div>
      </div>
    `;
  }

  renderLists() {
    let inBoxHTML = '';
    let addMoreHTML = '';
    
    let hasInBox = false;
    
    for (const [id, data] of Object.entries(this.productsData)) {
      const qty = this.selections[id] || 0;
      if (qty > 0) {
        inBoxHTML += this.createItemHTML(id, data, qty, true);
        hasInBox = true;
      } else {
        addMoreHTML += this.createItemHTML(id, data, 0, false);
      }
    }
    
    this.inBoxList.innerHTML = inBoxHTML;
    this.addMoreList.innerHTML = addMoreHTML;
    this.inBoxTitle.style.display = hasInBox ? 'block' : 'none';
  }

  async handleCheckout() {
    if (!this.isReviewMode) {
      this.isReviewMode = true;
      this.freqSection.classList.add('active');
      this.updateUI();
      
      // Add a subtle click pulse animation to the button
      this.checkoutBtn.classList.add('pulse');
      setTimeout(() => this.checkoutBtn.classList.remove('pulse'), 200);
      
      return;
    }
    
    // Proceed to checkout
    this.checkoutBtn.disabled = true;
    this.checkoutBtn.classList.remove('ob-cta-pulse');
    const originalText = this.checkoutBtn.textContent;
    this.checkoutBtn.textContent = 'Adding to cart...';
    
    const frequencyDays = this.freqSelect.value; // "30", "14", "7", "onetime"
    
    const items = [];
    
    // Add the individual fish directly with their specific selling plan IDs
    for (const [id, qty] of Object.entries(this.selections)) {
      const product = this.productsData[id];
      const payload = {
        id: product.variantId,
        quantity: qty
      };
      
      if (frequencyDays !== 'onetime') {
        const plans = product.selling_plans || {};
        let planId = null;
        for (const [name, pid] of Object.entries(plans)) {
          if (name.includes(frequencyDays + ' Day') || name.includes(frequencyDays + ' day')) {
            planId = pid;
            break;
          }
        }
        if (!planId) {
          planId = Object.values(plans)[0];
        }
        if (planId) {
          payload.selling_plan = planId;
        }
      }
      
      items.push(payload);
    }
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Shopify Cart Error:', errorData);
        alert('Could not add bundle to cart: ' + (errorData.description || errorData.message || 'Unknown error'));
        this.checkoutBtn.disabled = false;
        this.checkoutBtn.classList.add('ob-cta-pulse');
        this.checkoutBtn.textContent = originalText;
        return;
      }
      
      window.location.href = '/checkout';
    } catch (e) {
      console.error('Error adding to cart:', e);
      this.checkoutBtn.disabled = false;
      this.checkoutBtn.classList.add('ob-cta-pulse');
      this.checkoutBtn.textContent = originalText;
      alert('There was a network error adding items to your cart. Please try again.');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.byobDrawer = new BYOBDrawer();
});
